import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { company } from "../data/companyData";
import "./styles/ProductModal.css";

/**
 * Right-hand detail drawer for a single product. Deep-linkable: the Products
 * page drives `product` from the /products/:slug route, so this drawer IS the
 * content of a product page — hence the product name is its <h1> and the
 * catalogue heading behind it steps down to a <p>.
 */
const ProductModal = ({ product, onClose }) => {
  const panelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!product) return;
    document.body.classList.add("no-scroll");
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const id = requestAnimationFrame(() => panelRef.current?.focus());
    return () => {
      document.body.classList.remove("no-scroll");
      window.removeEventListener("keydown", onKey);
      cancelAnimationFrame(id);
    };
  }, [product, onClose]);

  if (!product) return null;

  const quoteMessage = `I would like to enquire about ${product.name} (${product.grade}, ${product.purity}). Please share pricing, availability and specifications.`;

  const requestQuote = () => {
    onClose();
    navigate("/contact", {
      state: { productName: product.name, message: quoteMessage },
    });
  };

  const whatsapp = `https://wa.me/${company.phones.whatsapp.tel.replace(
    "+",
    ""
  )}?text=${encodeURIComponent(quoteMessage)}`;

  /* Portalled to <body>: the route-fade wrapper is transformed, which would
     otherwise become the containing block for this fixed overlay. */
  return createPortal(
    <div className="pmodal" role="dialog" aria-modal="true" aria-label={product.name}>
      <button
        type="button"
        className="pmodal__scrim"
        onClick={onClose}
        aria-label="Close product details"
        tabIndex={-1}
      />

      <div className="pmodal__panel" ref={panelRef} tabIndex={-1}>
        <button type="button" className="pmodal__close" onClick={onClose}>
          <span aria-hidden="true">✕</span>
          <span className="sr-only">Close</span>
        </button>

        <figure className="pmodal__media">
          <img src={product.image} alt={product.name} />
          <figcaption className="pmodal__formula mono">{product.formula}</figcaption>
        </figure>

        <div className="pmodal__body">
          <p className="pmodal__cat">{product.category}</p>
          <h1 className="pmodal__name">{product.name}</h1>
          <p className="pmodal__grade">
            {product.grade} · {product.appearance}
          </p>

          <p className="pmodal__price">Price on request</p>

          <p className="pmodal__desc">{product.description}</p>

          <div className="pmodal__section">
            <h3 className="pmodal__h">Specification</h3>
            <dl className="pmodal__specs">
              {product.specs.map((s) => (
                <div className="pmodal__spec" key={s.label}>
                  <dt>{s.label}</dt>
                  <dd>{s.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="pmodal__section">
            <h3 className="pmodal__h">Typical applications</h3>
            <ul className="pmodal__apps">
              {product.applications.map((a) => (
                <li key={a}>{a}</li>
              ))}
            </ul>
          </div>

          <div className="pmodal__actions">
            <button type="button" className="btn btn--accent" onClick={requestQuote}>
              Request a quote
              <span className="btn__arrow" aria-hidden="true">→</span>
            </button>
            <a
              href={whatsapp}
              target="_blank"
              rel="noreferrer"
              className="btn btn--ghost-light">
              <i className="fa-brands fa-whatsapp" aria-hidden="true" /> WhatsApp
            </a>
          </div>

          <p className="pmodal__note">
            Certificate of analysis supplied with every consignment. Bulk and
            part-load dispatch by road, sea, air and cargo.
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductModal;
